import app from '@frontastic/catwalk/js/app/app';
import Context from '@frontastic/catwalk/js/app/context';
import FrontasticRoute from '@frontastic/catwalk/js/app/route';
import { useEffect, useRef } from 'react';
import debounce from '../common/src/js/helper/debounce';
import { trackingPageView } from './reducer/node';
import { store } from './store';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { PageResponse } from '@commercetools/frontend-sdk/lib/types/api/page/PageResponse';

/*
 * The callback is only executed once during the initial rendering and never again.
 * In contrast to useEffect, this hook calls the callback during rendering and not afterward.
 */
const useOnlyExecuteOnInitialRender = (callback: () => void) => {
  const isInitialRenderRef = useRef(true);

  if (!isInitialRenderRef.current) {
    return;
  }

  callback();
  isInitialRenderRef.current = false;
};

export function useFrontasticBridgeReduxLayoutDispatches(locale: string, userAgent: string, contextData: any | null) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useOnlyExecuteOnInitialRender(() => {
    const isServerSide = typeof window === 'undefined';

    store.dispatch({
      type: 'FRONTASTIC_BRIDGE_INIT',
    });

    if (isServerSide) {
      store.dispatch({
        type: 'FRONTASTIC_BRIDGE_SERVER',
      });
    } else {
      store.dispatch({
        type: 'FRONTASTIC_BRIDGE_CLIENT',
      });
    }

    app.initialize(router, new Context(contextData));

    let route = { route: null, parameters: [] };
    try {
      route = app.getRouter().match(pathname);
    } catch (e) {
      // ignore
    }

    // @COFIXME[error-handling](FLBML-135): Properly handle non page results (I got an error here on a 500 in the page request)
    store.dispatch({
      type: 'FRONTASTIC_ROUTE',
      // @COFXIME: How do we want to handle multiple search params with the same name?
      route: new FrontasticRoute(route, Object.fromEntries(searchParams), null),
      lastRoute: null,
    });

    store.dispatch({
      type: 'ApiBundle.Api.context.success',
      data: {
        ...contextData,
        locale: locale,
      },
    });

    store.dispatch({
      type: 'Frontastic.RenderContext.UserAgentDetected',
      userAgent: userAgent,
    });
  });

  useEffect(() => {
    // After the initial render, we want to dispatch the viewport dimensions
    // Before this, we **should** use the user agent from SSR
    const dispatchViewportDimensions = () => {
      store.dispatch({
        type: 'Frontastic.RenderContext.ViewportDimensionChanged',
        viewportDimension: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      });
    };

    dispatchViewportDimensions();
    window.addEventListener('resize', debounce(dispatchViewportDimensions, 500));

    // After the initial render, we dispatch that this is a client side render
    // Before the initial render it needs to run in SSR mode, otherwise we'd have a hydration diff.
    store.dispatch({
      type: 'Frontastic.RenderContext.ClientSideDetected',
    });
  }, []);

  useEffect(() => {
    // The router instance returned by `useRouter` can change so we update it in the app
    app.updateRouter(router);
  }, [router]);
}

export function useFrontasticBridgeReduxDispatches(pageData: PageResponse) {
  useOnlyExecuteOnInitialRender(() => {
    // The useOnlyExecuteOnInitialRender() callback can be executed multiple times under some conditions. That only
    // happens during client side navigation. No idea why that happens. Probably because of some Next.js hack which
    // interferes with our hack.
    // So we use `window` as a global variable here to ensure that we don't dispatch the action multiple times.
    // This works as expected. There is nothing to see here. Please move along.
    if (typeof window !== 'undefined' && window['frontasticBridgeReduxDispatchesAlreadyExecuted'] === true) {
      return;
    }

    if (pageData?.pageFolder?.pageFolderId) {
      store.dispatch({
        type: 'Frontend.Node.initialize',
        data: {
          node: { ...pageData.pageFolder, nodeId: pageData.pageFolder.pageFolderId },
          page: pageData.page,
          data: pageData.data,
          route: {
            // @COFIXME[routing](FLBML-125): No idea where the route data should come from, but not from pageData :D
            // ...pageData.route,
            parameters: {},
          },
        },
      });
    }

    if (typeof window !== 'undefined') {
      window['frontasticBridgeReduxDispatchesAlreadyExecuted'] = true;
    }
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Reset after the page was fully rendered so that the dispatches do execute on the next page again.
      window['frontasticBridgeReduxDispatchesAlreadyExecuted'] = false;
    }
  }, []);

  useEffect(() => {
    // @COFIXME[error-handling](FLBML-136): Properly handle non page results (404s and maybe redirects)
    if (!pageData?.pageFolder?.pageFolderId) {
      return;
    }

    store.dispatch(
      trackingPageView({
        nodeId: pageData.pageFolder.pageFolderId,
        data: {
          node: pageData.pageFolder,
          page: pageData.page,
          data: pageData.data,
        },
        // @COFIXME(FLBML-139): Determine if this is correct
        isMasterPage: pageData.pageFolder.isDynamic,
      }),
    );
    // The following is a workaround to execute this only if the pageFolderId changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageData?.pageFolder?.pageFolderId]);
}
